import {
	faLock,
	faPen,
	faTimes,
	faUnlock,
	faUpload,
} from '@fortawesome/free-solid-svg-icons'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button, DialogWrapper } from '@/components'
import { StructureObjectDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useAuthContext,
} from '@/hooks'
import { lockNode } from '@/store/modules/node/actions'
import {
	cancelEditingTab,
	editTab,
	setTabPermanent,
} from '@/store/modules/tab/actions'
import {
	canUnlockStructure,
	hasNodePermissions,
	hasStructureEditPermission,
	hasStructureManageAccessPermission,
	hasStructureViewPermission,
	isEditable,
	isImportableNodeType,
	isLocked,
	isLockedByMe,
	shouldShowUnlockButton,
} from '@/utils'

import { CancelEditModal } from '../CancelEditModal'
import { DeleteModal } from '../DeleteModal'
import { useDetailTabContext } from '../DetailTab/context/DetailTabContext'
import { NodeButtons } from '../NodeButtons'
import { PermissionsButton } from '../PermissionsButton'
import { PushModal } from '../PushModal/PushModal'
import { ExportStructure } from '../TreePanel/components/ExportStructure'
import { ImportStructureModal } from '../TreePanel/components/ImportStructureModal'
import { UnlockModal } from '../UnlockModal'
import { EditableNodeActionsProps } from './types'

export const EditableNodeActions = ({
	node,
	editMode,
	dirty,
	buttons,
	moreButtons,
	onCancel,
	onEdit,
	contentCustom,
	afterPush,
	beforePush,
	customDiagramWarning,
}: EditableNodeActionsProps) => {
	const { t } = useAppContext()
	const auth = useAuthContext()

	const dispatch = useAppDispatch()
	const user = useAppStore((state) => state.auth.user)
	const [download, setDownload] = useState(false)
	const [importModalOpen, setImportModalOpen] = useState(false)

	const {
		state: { version },
	} = useDetailTabContext()

	const edit = async () => {
		dispatch(setTabPermanent(node))
		await dispatch(lockNode(node))
		await onEdit(node)
		dispatch(editTab(node))
	}

	const lock = async () => {
		await dispatch(lockNode(node))
		dispatch(setTabPermanent(node))
	}

	useEffect(() => {
		if (editMode && !isEditable(node, user)) {
			dispatch(cancelEditingTab(node))
		}
	}, [dispatch, editMode, node, user])

	const canDelete =
		node.type === StructureObjectDto.TypeEnum.SYSTEM
			? auth.canDeleteSystem()
			: isEditable(node, user)

	const canExportStructure = hasStructureViewPermission(node)
	const canImport =
		hasStructureEditPermission(node) && isImportableNodeType(node.type)

	const showMoreMenu =
		(hasNodePermissions(node.type) &&
			hasStructureManageAccessPermission(node)) ||
		!!moreButtons ||
		canDelete ||
		canExportStructure ||
		canImport

	return (
		<>
			{!editMode && (
				<>
					{contentCustom}
					<NodeButtons
						buttons={
							<>
								{typeof buttons === 'function'
									? buttons({
											isLocked: isLocked(node),
											isInEditMode: editMode,
											isLockedByMe: !!isLockedByMe(node, user),
											isEditable: !!isEditable(node, user),
										})
									: buttons}
								<Button
									disabled={!isEditable(node, user) || version !== undefined}
									onClick={edit}
									icon={faPen}
									schema="primary"
									coloredIcon={true}
									name="edit-node"
								>
									{t('EDIT')}
								</Button>

								{shouldShowUnlockButton(node, user) && (
									<DialogWrapper
										dialog={(opened, onClose) =>
											opened && <UnlockModal node={node} onClose={onClose} />
										}
									>
										{(onClick) => (
											<Button
												disabled={!canUnlockStructure(node, user)}
												onClick={onClick}
												icon={faUnlock}
												schema="warn"
												coloredIcon={true}
												name="unlock-node"
											>
												{t('UNLOCK')}
											</Button>
										)}
									</DialogWrapper>
								)}

								{!isLocked(node) && (
									<Button
										disabled={!hasStructureEditPermission(node)}
										onClick={lock}
										icon={faLock}
										schema="warn"
										coloredIcon={true}
										name="lock-node"
									>
										{t('LOCK')}
									</Button>
								)}
							</>
						}
						moreButtons={
							showMoreMenu && (
								<>
									{typeof moreButtons === 'function'
										? moreButtons({
												isLocked: isLocked(node),
												isInEditMode: editMode,
												isLockedByMe: !!isLockedByMe(node, user),
												isEditable: !!isEditable(node, user),
											})
										: moreButtons}

									<MenuItem
										disabled={!canExportStructure}
										onClick={() => setDownload(true)}
									>
										<ListItemIcon>
											<DownloadIcon fontSize="small" />
										</ListItemIcon>
										<ListItemText>{t('EXPORT')}</ListItemText>
									</MenuItem>

									{canImport && (
										<MenuItem onClick={() => setImportModalOpen(true)}>
											<ListItemIcon>
												<FileUploadIcon fontSize="small" />
											</ListItemIcon>
											<ListItemText>{t('IMPORT')}</ListItemText>
										</MenuItem>
									)}

									{hasNodePermissions(node.type) &&
										hasStructureManageAccessPermission(node) && (
											<PermissionsButton node={node} />
										)}

									{canDelete && (
										<DialogWrapper
											dialog={(opened, onClose) =>
												opened && <DeleteModal node={node} onClose={onClose} />
											}
										>
											{(onClick) => (
												<MenuItem onClick={onClick}>
													<ListItemIcon>
														<DeleteIcon fontSize="small" color="error" />
													</ListItemIcon>
													<ListItemText sx={{ color: 'error.main' }}>
														{t('DELETE')}
													</ListItemText>
												</MenuItem>
											)}
										</DialogWrapper>
									)}
								</>
							)
						}
					/>
				</>
			)}
			{editMode && (
				<NodeButtons
					buttons={
						<>
							{dirty !== undefined && (
								<DirtyInfo
									data-testid={`autosave-info-${dirty ? 'saving' : 'saved'}`}
								>
									{t(dirty ? 'AUTOSAVE_DIRTY' : 'AUTOSAVE_SAVED')}
								</DirtyInfo>
							)}
							{contentCustom}
							<DialogWrapper
								dialog={(opened, onClose) =>
									opened && (
										<PushModal
											node={node}
											onClose={onClose}
											onExitEditMode={onCancel}
											beforePush={beforePush}
											afterPush={afterPush}
										/>
									)
								}
							>
								{(onClick) => (
									<Button
										icon={faUpload}
										onClick={onClick}
										schema="success"
										coloredIcon={true}
										testId="push-changes"
									>
										{t('PUSH')}
									</Button>
								)}
							</DialogWrapper>

							<DialogWrapper
								dialog={(opened, onClose) =>
									opened && (
										<CancelEditModal
											node={node}
											onClose={onClose}
											onConfirm={onCancel}
											customDiagramWarning={customDiagramWarning}
										/>
									)
								}
							>
								{(onClick) => (
									<Button
										icon={faTimes}
										onClick={onClick}
										schema="danger"
										coloredIcon={true}
										testId="cancel-changes"
									>
										{t('CANCEL')}
									</Button>
								)}
							</DialogWrapper>
						</>
					}
					moreButtons={
						moreButtons && (
							<>
								{typeof moreButtons === 'function'
									? moreButtons({
											isLocked: isLocked(node),
											isInEditMode: editMode,
											isLockedByMe: !!isLockedByMe(node, user),
											isEditable: !!isEditable(node, user),
										})
									: moreButtons}
							</>
						)
					}
				/>
			)}

			{download && (
				<ExportStructure
					nodeId={node.id}
					onDownloadFinish={() => setDownload(false)}
				/>
			)}

			{importModalOpen && (
				<ImportStructureModal
					node={node}
					onClose={() => setImportModalOpen(false)}
				/>
			)}
		</>
	)
}

const DirtyInfo = styled.div`
	margin: auto 12px auto 6px;
`
